import { supertypeClass, property } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import { RiskFactors } from './RiskFactors';
import { Assumptions } from '../static/Assumptions';
import { Customer } from './Customer';
import { Utils } from '../Utils';
import { TravelCountry as GQLTravelCountry } from '@hit/hit-api-client';
import { Policy } from './Policy';

@supertypeClass({ toClient: true, toServer: true })
export class TravelCountry extends Everything implements GQLTravelCountry {
  @property({ getType: () => RiskFactors })
  riskFactors: RiskFactors;

  @property({
    rule: ['required'],
    values: function () {
      const countries: string[] = Assumptions.allCountries;
      const policy = this.customer.application.primaryPolicy;
      const channelCode: string = policy.getChannelCode();
      if (Utils.isCoverpathPlatform(channelCode) && countries.indexOf('Other') === -1) {
        countries.push('Other');
      }
      return countries;
    },
    descriptions: function () {
      const keys = this.__values__('name');
      const ret = {};
      this.__values__('name').forEach((val) => (ret[val] = val));
      return ret;
    },
  })
  name: string;

  @property({
    rule: ['required'],
    comment:
      "User-specified input to complement the countries property, e.g. if 'Other' is selected, the user can specific details here.",
  })
  ftOtherCountry: string;

  @property({
    rule: ['required'],
    values: function () {
      return this.mexicoValues;
    },
  })
  ftMexicoSpecific: string;

  @property({
    rule: ['required'],
  })
  ftMexicoSpecificOther: string;

  @property({ rule: ['required'], values: ['true', 'false'], descriptions: { true: 'Yes', false: 'No' } })
  departureDateWhen: boolean;

  @property({ rule: ['required', 'futuredate'] })
  departureDate: Date;

  // TODO: the following is  a dup property, created - need to have a migration to remove it and sync up with old one
  @property({ rule: ['futuredate'] })
  departureDateNY: Date;

  departureDateTrigger(): void {
    this.departureDateNY = this.departureDate;
  }

  departureDateNYTrigger(): void {
    this.departureDate = this.departureDateNY;

    if (this.departureDateNY) {
      this.departureDateWhen = true;
    } else {
      this.departureDateWhen = false;
    }
  }

  noDepartureDateNYTrigger(): void {
    if (this.noDepartureDateNY) {
      this.departureDateNY = null;
      this.departureDate = null;
    }
  }

  @property()
  noDepartureDateNY: boolean = false;

  @property({ rule: ['required', 'numeric'], validate: 'isWithin(1,1000)' })
  duration: number;

  @property({
    rule: ['required'],
    values: ['Pleasure', 'Business/Work', 'Education', 'Other'],
    descriptions: function () {
      const value = {
        'Business/Work': 'Business/Work',
        'Education': 'Education',
        'Other': 'Other',
      };
      if (this.customer && this.customer.application.primaryPolicy.isICCApplication()) {
        value['Pleasure'] = 'Pleasure/Family';
      } else {
        value['Pleasure'] = 'Pleasure';
      }
      return value;
    },
  })
  purpose: string;

  @property({
    rule: ['required'],
  })
  purposeDetails: string;

  @property({ isLocal: true, getType: () => String })
  aRatedCountryValues: Array<string> = [
    'Albania',
    'American Samoa',
    'Andorra',
    'Anguilla',
    'Antigua and Barbuda',
    'Argentina',
    'Aruba',
    'Australia',
    'Austria',
    'Azores',
    'Barbados',
    'Belgium',
    'Bermuda',
    'Bosnia and Herzegovina',
    'Brunei Darussalam',
    'Bulgaria',
    'Canada',
    'Canary Islands',
    'Cayman Islands',
    'Chile',
    'Cook Islands',
    'Croatia',
    'Curacao',
    'Cyprus',
    'Czech Republic',
    'Denmark',
    'Dominica',
    'Estonia',
    'Falkland Islands',
    'Finland',
    'France',
    'French Polynesia',
    'Germany',
    'Greece',
    'Greenland',
    'Grenada',
    'Guadeloupe',
    'Holland',
    'Hong Kong',
    'Hungary',
    'Iceland',
    'Ireland',
    'Israel (excluding Gaza and West Bank)',
    'Italy',
    'Japan',
    'South Korea',
    'Kuwait',
    'Latvia',
    'Liechtenstein',
    'Lithuania',
    'Luxembourg',
    'Macau',
    'Macedonia',
    'Madeira',
    'Malta',
    'Marshall Islands',
    'Martinique',
    'Monaco',
    'Montenegro',
    'Montserrat',
    'Netherlands',
    'Netherlands Antilles',
    'New Caledonia',
    'New Zealand',
    'Northern Ireland',
    'Northern Mariana Islands',
    'Norway',
    'Palau',
    'Poland',
    'Portugal',
    'Qatar',
    'Romania',
    'Saipan',
    'San Marino',
    'Serbia',
    'Singapore',
    'Slovakia',
    'Slovenia',
    'Spain',
    'Saint Kitts and Nevis',
    'Saint Lucia',
    'Saint Martin',
    'Saint Thomas',
    'Saint Vincent and the Grenadines',
    'Sweden',
    'Switzerland',
    'Taiwan',
    'Turks and Caicos',
    'United Arab Emirates',
    'United Kingdom',
    'Uruguay',
    'Virgin Islands',
  ];

  @property({ isLocal: true, getType: () => String })
  bRatedCountryValues: Array<string> = [
    'Armenia',
    'Azerbaijan',
    'Bahamas',
    'Belarus',
    'Bhutan',
    'Brazil',
    'Cape Verde',
    'China',
    'Costa Rica',
    'Ecuador',
    'Micronesia',
    'Fiji',
    'French Guiana',
    'Georgia',
    'Jamaica',
    'Jordan',
    'Kazakhstan',
    'Kosovo',
    'Maldives',
    'Mauritius',
    'Mexico',
    'Micronesia',
    'Mexico',
    'Moldova',
    'Mongolia',
    'Niue',
    'Oman',
    'Panama',
    'Paraguay',
    'Peru',
    'Russia (excluding Chechnya)',
    'Samoa',
    'Seychelles',
    'Suriname',
    'Tonga',
    'Trinidad and Tobago',
    'Turkey',
    'Ukraine',
    'Yugoslavia',
  ];

  @property({ isLocal: true, getType: () => String })
  cRatedCountryValues: Array<string> = [
    'Bangladesh',
    'Belize',
    'Bolivia',
    'Botswana',
    'Comoros',
    'Dominican Republic',
    'El Salvador',
    'Guatemala',
    'Guyana',
    'Honduras',
    'India',
    'Indonesia',
    'Kiribati',
    'Kyrgyzstan',
    'Laos',
    'Lebanon',
    'Morocco',
    'Namibia',
    'Nauru',
    'Nepal',
    'Nicaragua',
    'Philippines',
    'Saudi Arabia',
    'Solomon Islands',
    'South Africa',
    'Sri Lanka',
    'Tajikistan',
    'Thailand',
    'Turkmenistan',
    'Tuvalu',
    'Uzbekistan',
    'Vanuatu',
    'Venezuela',
    'Vietnam',
  ];

  @property({ isLocal: true, getType: () => String })
  mexicoValues: Array<string> = [
    'Cabo San Lucas',
    'Manzanillo',
    'Quintana Roo',
    'Acapulco',
    'Puerto Vallarta',
    'Mexico City',
    'Guanajuato (including Leon)',
    'Monterrey',
    'Cancun',
    'San Jose del Cabo',
    'Playa del Carmen',
    'Puerto Escondido',
    'Cozumel',
    'Guadalajara',
    'Other',
  ];

  @property({ getType: () => Customer })
  customer: Customer;

  constructor() {
    super();
  }

  static new(customer: Customer): TravelCountry {
    return new TravelCountry().init(customer);
  }

  init(customer: Customer): this {
    this.customer = customer;

    return this;
  }

  nameTrigger(): void {
    if (this.name !== 'Mexico') {
      this.ftMexicoSpecific = '';
    }
  }

  forBusiness(): boolean {
    return this.purpose === 'Business/Work';
  }

  forPleasure(): boolean {
    return this.purpose === 'Pleasure';
  }

  toString(): string {
    return (
      (this.ftMexicoSpecific ? this.ftMexicoSpecific + ', ' : '') +
      this.name +
      ' for ' +
      this.duration +
      ' days' +
      (this.departureDate ? ' departing on ' + Utils.dateToMdy(this.departureDate) : '')
    );
  }

  ftMexicoSpecificTrigger(): void {
    const primaryPolicy: Policy = this.customer && this.customer.application && this.customer.application.primaryPolicy;
    const channelCode: string = primaryPolicy && primaryPolicy.getChannelCode();
    const isCoverpathPlatform: boolean = Utils.isCoverpathPlatform(channelCode);
    const isNewYorkApplication: boolean = primaryPolicy && primaryPolicy.getApplicationState() === 'NY';
    const valueIsInOptions: boolean = this.mexicoValues.includes(this.ftMexicoSpecific);

    if (isCoverpathPlatform && isNewYorkApplication && !valueIsInOptions) {
      this.ftMexicoSpecific = 'Other';
    }
  }
}
